import { useEffect, useMemo, useState } from 'react'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspace } from '../../context/useWorkspace'
import { subscribeActiveQuotas } from '../../services/quotaService'
import { QUOTA_TARGET_TYPES, quotaAttainment, type Quota, type QuotaTargetType } from '../../types/quota'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { Button } from '../Common'
import { HelperEmptyStateSlim } from './HelperEmptyState'
import { computeHelperMeInsight } from '../../services/helper/helperMeInsights'
import { isModuleEnabled } from '../../utils/workspaceModules'

import {
  type HelperQuotaProjection,
  dayKey,
  dateFromValue,
  buildHelperQuotaProjection,
} from './helperQuotaUtils'

export type { HelperQuotaProjection }

const targetLabel = (target: QuotaTargetType, t: ReturnType<typeof useNls>['t']) => {
  if (target === 'tasks_completed') return t('NLSHelperQuotaTargetTasks')
  if (target === 'sales_units') return t('NLSHelperQuotaTargetSales')
  if (target === 'checklists_done') return t('NLSHelperQuotaTargetChecklists')
  if (target === 'hours_logged') return t('NLSResourcesQuotaTargetHoursLogged')
  return t('NLSHelperQuotaTargetWeighted')
}

const formatQuotaDateRange = (quota: Quota) => {
  const start = dateFromValue(quota.startDate)
  const end = dateFromValue(quota.endDate)
  if (!start && !end) return ''
  if (!start) return dayKey(end as Date)
  if (!end) return dayKey(start)
  return `${dayKey(start)} - ${dayKey(end)}`
}

export const HelperQuotaPanel = ({
  workspaceId,
  actor,
  items,
}: {
  workspaceId: string
  actor: { id: string; name: string } | null
  items: HelperAssignedItem[]
}) => {
  const { t } = useNls()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const [quotas, setQuotas] = useState<Quota[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryToken, setRetryToken] = useState(0)
  const quotaEnabled = isModuleEnabled(
    workspaces.find((workspace) => workspace.id === activeWorkspaceId),
    'quota',
  )

  useEffect(() => {
    return subscribeActiveQuotas(
      workspaceId || '',
      actor?.id,
      (next) => {
        setQuotas(next)
        setLoading(false)
        setError(null)
      },
      (nextError) => {
        setError(nextError.message)
        setLoading(false)
      },
    )
  }, [actor?.id, workspaceId, retryToken])

  const projection = useMemo(() => buildHelperQuotaProjection(items), [items])
  const projectionEntries = QUOTA_TARGET_TYPES
    .map((target) => ({ target, value: projection[target] ?? 0 }))
    .filter((entry) => entry.value > 0)

  const activeQuota = quotas[0] ?? null
  const attainment = activeQuota ? quotaAttainment(activeQuota) : 0
  const percent = Math.round(attainment * 100)
  const targetEntries = activeQuota
    ? QUOTA_TARGET_TYPES.map((target) => ({ target, entry: activeQuota.targets[target] })).filter((entry) => entry.entry)
    : []

  const insight = computeHelperMeInsight({
    card: 'QUOTA',
    quota: { enabled: quotaEnabled, percentComplete: percent, error: error !== null },
  })

  if (!quotaEnabled) {
    return <section className="relative overflow-hidden rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-card)]">
      <header className="flex items-start justify-between"><div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">{t('NLSHelperQuotaEyebrow')}</p><p className="mt-1 text-3xl font-semibold text-[var(--text-strong)]">—</p></div></header>
      <p className="mt-3 text-xs font-medium text-[var(--muted)]">{insight}</p><HelperEmptyStateSlim title={t('NLSHelperMeQuotaOffTitle')} body={t('NLSHelperMeQuotaOffBody')} className="mt-4 px-0 py-0" />
    </section>
  }

  return (
    <section className="relative overflow-hidden rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-card)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[var(--accent)]">
            <TrackChangesRoundedIcon style={{ fontSize: 14 }} /> {t('NLSHelperQuotaEyebrow')}
          </p>
          <p className="mt-1 text-3xl font-semibold leading-none text-[var(--text-strong)]">{percent}%</p>
          {activeQuota ? <p className="mt-1 text-[11px] font-semibold text-[var(--muted)]">{formatQuotaDateRange(activeQuota)}</p> : null}
        </div>
      </div>
      <p className="mt-3 text-xs font-medium leading-relaxed text-[var(--muted)]">{insight}</p>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-[var(--surface-muted)]">
        <div className="h-full rounded-full bg-gradient-to-r from-[var(--btn-primary-from)] to-[var(--btn-primary-to)]" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
      </div>

      {loading ? (
        <p className="mt-4 text-sm font-semibold text-[var(--muted)]">{t('NLSHelperQuotaLoading')}</p>
      ) : error ? (
        <Button
          variant="link"
          size="sm"
          tone="danger"
          className="mt-4"
          onClick={() => {
            setLoading(true)
            setRetryToken((token) => token + 1)
          }}
        >
          {t('NLSHelperMeQuotaRetry')}
        </Button>
      ) : !activeQuota ? (
        <HelperEmptyStateSlim title={t('NLSHelperQuotaEmpty')} className="mt-4 px-0 py-0" />
      ) : targetEntries.length === 0 ? (
        <HelperEmptyStateSlim title={t('NLSHelperQuotaNoTargets')} className="mt-4 px-0 py-0" />
      ) : (
        <div className="mt-4 grid gap-2">
          {targetEntries.map(({ target, entry }) => {
            const current = entry?.currentValue ?? 0
            const targetValue = entry?.targetValue ?? 0
            const ratio = targetValue > 0 ? Math.min(100, Math.round((current / targetValue) * 100)) : 0
            return (
              <div key={target} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-bold text-[var(--text)]">{targetLabel(target, t)}</span>
                  <strong className="font-black text-[var(--text-strong)]">{current} / {targetValue}</strong>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--border-subtle)]">
                  <div className="h-full rounded-full bg-[var(--success)] transition-[width] duration-700 ease-out" style={{ width: `${ratio}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-[color-mix(in_srgb,var(--info)_20%,transparent)] bg-[var(--info-soft)] p-3">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]">
          <TodayRoundedIcon style={{ fontSize: 14 }} /> {t('NLSHelperQuotaTodayTitle')}
        </p>
        {projectionEntries.length === 0 ? (
          <HelperEmptyStateSlim title={t('NLSHelperQuotaTodayEmpty')} className="mt-2 px-0 py-0" />
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {projectionEntries.map((entry) => (
              <span key={entry.target} className="inline-flex items-center gap-1 rounded-full border border-[color-mix(in_srgb,var(--info)_25%,transparent)] bg-[var(--surface-card)] px-2.5 py-1 text-[11px] font-black text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]">
                <TrendingUpRoundedIcon style={{ fontSize: 13 }} /> +{entry.value} {targetLabel(entry.target, t)}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
